import { FunctionComponent } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { Box, Stack, Text, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import {
  getProductDisplayName,
  ProductIcon,
  sortProducts,
} from '@app/app-updates-product-app';
import { UiProductType } from '~src/packages/app-updates-product-app/src/types';
import type { UpdateType } from '@app/app-updates/src/schema';
import { UpdateImpactRating } from '@app/app-updates-table/src/components/UpdateImpactRating';
import { UpdateTags } from '@app/app-updates-table/src/components/UpdateTags';
import { fg, featureGateKeys } from '@app/feature-gates';

export const messages = defineMessages({
  appsImpactedTitle: {
    id: 'appUpdatesDetails.sidebar.appsImpactedTitle',
    defaultMessage: 'Apps impacted',
    description: 'Title for the section displaying apps impacted by the update',
  },
  impactRatingTitle: {
    id: 'appUpdatesDetails.sidebar.impactRatingTitle',
    defaultMessage: 'Change impact',
    description: 'Title for the impact rating section in the sidebar',
  },
  changeTypeTitle: {
    id: 'appUpdatesDetails.sidebar.changeTypeTitle',
    defaultMessage: 'Contextual tags',
    description: 'Title for the change type (tags) section in the sidebar',
  },
});

const titleStyles = xcss({
  marginBlockEnd: 'space.100',
});

const contentStyles = xcss({
  fontWeight: 'font.weight.regular',
  color: 'color.text.subtlest',
  marginBlockStart: 'space.150',
  gap: 'space.100',
});

const productItemStyles = xcss({
  display: 'flex',
  alignItems: 'center',
  gap: 'space.100',
});

export interface AppImpactedSidebarProps {
  /**
   * Array of products impacted by the update
   */
  products?: readonly UiProductType[] | null;
  /**
   * AI-generated impact rating for the update
   */
  impactRating?: UpdateType['impactRating'];
  /**
   * AI-generated contextual tags for the update
   */
  tags?: UpdateType['tags'];
}

/**
 * Sidebar component that displays the list of apps impacted by an update.
 * Shows only the app names and icons in a clean, simplified layout.
 *
 * Example:
 * ```tsx
 * <AppImpactedSidebar
 *   products={['JIRA', 'CONFLUENCE', 'BITBUCKET']}
 * />
 * ```
 */
export const AppImpactedSidebar: FunctionComponent<AppImpactedSidebarProps> = ({
  products,
  impactRating,
  tags,
}) => {
  const { formatMessage } = useIntl();

  const hasProducts = products && products.length > 0;
  const hasTags = tags && tags.length > 0;
  const shouldShowContextualTags = fg(featureGateKeys.contextualTagsBeta);
  const hasVisibleImpactRating = Boolean(impactRating);
  const hasVisibleTags = shouldShowContextualTags && hasTags;

  // Don't render if nothing to show
  if (!hasProducts && !hasVisibleImpactRating && !hasVisibleTags) {
    return null;
  }

  return (
    <Stack space="space.300">
      {/* Impact Rating (AI-generated) */}
      {hasVisibleImpactRating && (
        <section data-testid="app-impacted-sidebar-impact-rating">
          <Box xcss={titleStyles}>
            <Heading as="div" size="small">
              {formatMessage(messages.impactRatingTitle)}
            </Heading>
          </Box>
          <Box xcss={contentStyles}>
            <UpdateImpactRating impactRating={impactRating} />
          </Box>
        </section>
      )}

      {/* Contextual Tags (AI-generated, behind feature gate) */}
      {hasVisibleTags && (
        <section data-testid="app-impacted-sidebar-tags">
          <Box xcss={titleStyles}>
            <Heading as="div" size="small">
              {formatMessage(messages.changeTypeTitle)}
            </Heading>
          </Box>
          <Box xcss={contentStyles}>
            <UpdateTags tags={tags} />
          </Box>
        </section>
      )}

      {/* Apps Impacted */}
      {hasProducts && (
        <section data-testid="app-impacted-sidebar-products">
          <Box xcss={titleStyles}>
            <Heading as="div" size="small">
              {formatMessage(messages.appsImpactedTitle)}
            </Heading>
          </Box>
          <Stack xcss={contentStyles}>
            {sortProducts(products!).map((productType) => (
              <Box
                key={productType}
                xcss={productItemStyles}
                testId={`app-display-${productType}`}
              >
                <ProductIcon
                  productType={productType}
                  size="small"
                  testId="product-list-primary-icon"
                />
                <Text>{getProductDisplayName(productType)}</Text>
              </Box>
            ))}
          </Stack>
        </section>
      )}
    </Stack>
  );
};

export default AppImpactedSidebar;
